import type { Showing } from '@snapshots/domain';

/** What the list shows: the words searched and the page (`Collection`'s state, with the state filter apart) */
export interface View {
	query: string;
	page: number;
}

/**
 * The overview's address: where the list is (`project` or `organization`, or every organization of
 * the person), which snapshots it holds (`state`) and how they are shown (`q`, `page`), keeping the product
 * it was opened from (`from`). Everything a person narrowed, searched or paged lives here, so a reload, a
 * shared link and back and forward return to the same list. The list is ordered by when each snapshot was
 * made, newest first, as the service keeps it; an earlier address with `sort` and `direction` opens it
 * in that order too.
 */
export class Address {
	static #states: Showing[] = ['active', 'pending', 'failed', 'cancelled', 'ended', 'deleted'];

	#project?: string;
	#organization?: string;
	#from?: string;
	#state?: Showing;
	#view: View;

	constructor(parameters: URLSearchParams) {
		const value = (key: string) => parameters.get(key) || undefined;
		this.#project = value('project');
		this.#organization = this.#project ? undefined : value('organization');
		// The product the page was opened from (D54) stays while the list is narrowed, searched or paged
		this.#from = value('from');
		const state = value('state');
		this.#state = state && Address.#states.includes(state as Showing) ? (state as Showing) : undefined;
		const page = Number(value('page'));
		this.#view = { query: value('q') ?? '', page: Number.isInteger(page) && page > 1 ? page : 1 };
	}

	get project() {
		return this.#project;
	}

	get organization() {
		return this.#organization;
	}

	get view(): View {
		return this.#view;
	}

	/** Which list this is, whatever it shows: a change of scope is a new list */
	get scope(): string {
		return this.#project ? `project:${this.#project}` : this.#organization ? `organization:${this.#organization}` : 'all';
	}

	/** The state filter as the service reads it */
	get state(): Showing | undefined {
		return this.#state;
	}

	/** Whether this list shows deleted snapshots, whose columns differ */
	get deleted(): boolean {
		return this.#state === 'deleted';
	}

	/** The address of the same scope showing `view`, and `state` (this one's when not given) */
	with(view: View, state: Showing | undefined = this.#state): URLSearchParams {
		const parameters = new URLSearchParams();
		if (this.#project) parameters.set('project', this.#project);
		if (this.#organization) parameters.set('organization', this.#organization);
		if (state) parameters.set('state', state);
		if (view.query) parameters.set('q', view.query);
		if (view.page > 1) parameters.set('page', String(view.page));
		if (this.#from) parameters.set('from', this.#from);
		return parameters;
	}

	/** Whether going to `view` only changed the words: typing replaces the entry instead of adding one per pause */
	typed(view: View): boolean {
		return view.query !== this.#view.query && view.page === 1;
	}

	/**
	 * The capture a pasted address names (CNT-85): this interface's `/captures/<id>` or the service's
	 * `/v1/captures/<id>`, from any origin, as a full address or a path. Only the identifier is taken,
	 * and opening it is an ordinary visit to the capture, whose reading permission the service decides.
	 *
	 * @returns The capture's identifier, or undefined for words to search
	 */
	static capture(text: string): string | undefined {
		const value = text.trim();
		let path = value;
		try {
			path = new URL(value).pathname;
		} catch {
			if (!value.startsWith('/')) return undefined;
		}
		return /^(?:\/snapshots|(?:\/v1)?\/captures)\/(snp_[0-9a-f]{32})\/?$/.exec(path.split(/[?#]/)[0])?.[1];
	}

	/** The overview of one project, or of one organization */
	static of(place: { project?: string; organization?: string }): string {
		if (place.project) return `/?project=${encodeURIComponent(place.project)}`;
		return place.organization ? `/?organization=${encodeURIComponent(place.organization)}` : '/';
	}
}
